import { useEffect, useMemo } from 'react';
import {
  makeStyles,
  Theme,
  IconButton,
  Toolbar,
  Grid,
  Typography,
} from '@material-ui/core';
import { XCircle } from 'react-feather';
// design system
import { colors, NetworkIndicator } from 'design-system';
// models
import { EmissionResume } from 'src/models/emission/EmissionResume';
// data-layer-cr
import { useNetworkObserver } from 'data-layer-cr/dist/network';
// redux related
import { connect, ConnectedProps } from 'react-redux';
import {
  GET_EMISSION_RESUME,
  UPDATE_EMISSION_VIN,
} from 'src/store/emission/requestIdentifier';
import { RootState } from 'src/store/rootReducer';
import {
  cleanEmission,
  getEmissionPolicyDetailsById,
} from 'src/store/emission/actions';
// components
import {
  VinModificationForm,
  WaitingLayout,
  DownloadLayout,
  ErrorLayout,
} from './components';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    width: 444,
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
    height: '100vh',
    display: 'flex',
    flexFlow: 'column',
  },
  appBar: {
    minHeight: 100,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colors.madison,
    color: colors.white,
    position: 'relative',
  },
  closeButton: {
    position: 'absolute',
    top: theme.spacing(2),
    right: theme.spacing(2),
  },
  ownerText: {
    color: colors.white,
    fontSize: 19,
    fontWeight: 900,
  },
  carText: {
    color: colors.white,
    fontSize: 17,
    fontWeight: 700,
  },
  toolbar: {
    marginBottom: 20,
  },
  appBarContent: {
    marginTop: theme.spacing(1),
  },
}));

export const VinModificationLayout = ({
  // Redux props
  cleanEmissionAction,
  getEmissionPolicyDetailsAction,
  isEmissionLoaded,
  emission,
  // Local props
  quotationHash,
  onClose,
}: Props): JSX.Element => {
  const classes = useStyles();
  const getEmissionStatus = useNetworkObserver(GET_EMISSION_RESUME);
  const updateEmissionStatus = useNetworkObserver(UPDATE_EMISSION_VIN);

  const showError = useMemo(
    () => [getEmissionStatus, updateEmissionStatus].includes('failure'),
    [getEmissionStatus, updateEmissionStatus],
  );

  const showForm = useMemo(
    () => getEmissionStatus === 'success' && updateEmissionStatus === 'initial',
    [getEmissionStatus, updateEmissionStatus],
  );

  const showWaiting = useMemo(
    () =>
      getEmissionStatus === 'success' && updateEmissionStatus === 'requesting',
    [getEmissionStatus, updateEmissionStatus],
  );

  const showDownload = useMemo(
    () => getEmissionStatus === 'success' && updateEmissionStatus === 'success',
    [getEmissionStatus, updateEmissionStatus],
  );

  /**
   * Don't try to update if the provided id is -1, since this means that we
   * provided an "invalid" quotation.
   */
  useEffect(() => {
    if (quotationHash !== '') {
      getEmissionPolicyDetailsAction(quotationHash);
    }
  }, [quotationHash, getEmissionPolicyDetailsAction]);

  const handleClose = () => {
    cleanEmissionAction();
    onClose();
  };

  return (
    <div className={classes.root} data-testid="root-component">
      <nav className={classes.appBar}>
        {isEmissionLoaded && (
          <Toolbar data-testid="toolbar" className={classes.toolbar}>
            <Grid container spacing={1} className={classes.appBarContent}>
              <Grid item xs={12} container justifyContent="center">
                <Typography
                  data-testid="fullname"
                  className={classes.ownerText}
                >
                  {emission.fullname}
                </Typography>
              </Grid>
              <Grid item xs={12} container justifyContent="center">
                <Typography
                  data-testid="brand-model"
                  className={classes.carText}
                >
                  {`${emission.car.brand} ${emission.car.model} / ${emission.car.year}`}
                </Typography>
              </Grid>
            </Grid>
          </Toolbar>
        )}

        <IconButton
          id="vin-modification-drawer-close"
          data-testid="close-button"
          className={classes.closeButton}
          onClick={handleClose}
        >
          <XCircle strokeWidth="2px" color="#fff" />
        </IconButton>
      </nav>
      {/* Requesting screen */}
      <NetworkIndicator
        data-testid="network-indicator"
        requesting={getEmissionStatus === 'requesting'}
        message="Obteniendo detalle de poliza..."
      />

      {/* Form screen */}
      {showForm && <VinModificationForm data-testid="vin-modification-form" />}

      {/* Waiting screen */}
      {showWaiting && <WaitingLayout data-testid="vin-modification-waiting" />}

      {/* Download screen */}
      {showDownload && (
        <DownloadLayout data-testid="vin-modification-download" />
      )}

      {/* Error screen */}
      {showError && <ErrorLayout data-testid="vin-modification-error" />}
    </div>
  );
};

export const mapStateToProps = (
  state: RootState,
): {
  emission: EmissionResume;
  isEmissionLoaded: boolean;
} => ({
  emission: state.emission.emission,
  isEmissionLoaded: state.emission.isEmissionLoaded,
});

const reduxActions = {
  cleanEmissionAction: cleanEmission,
  getEmissionPolicyDetailsAction: getEmissionPolicyDetailsById,
};

const connector = connect(mapStateToProps, reduxActions);
type PropsFromRedux = ConnectedProps<typeof connector>;

interface Props extends PropsFromRedux {
  onClose: () => void;
  quotationHash: string;
}

export default connector(VinModificationLayout);
